/* Componentes compuestos de dominio (SGR_FRONTEND_ARCHITECTURE.md §5): ExposureMeter, FundBalanceCard, DeadlineChip, PerDiemQuantitySelector */
import type { ReactNode } from 'react';
import { Badge, Card, StatusBadge } from '@/shared/ui';
import { formatClp, formatDate } from '@/shared/lib/format';

export function ExposureMeter({ current, committed, projected, limit, label = 'Exposición FXR' }: { current: number; committed: number; projected?: number; limit: number; label?: string }) {
  const proj = projected ?? current + committed;
  const pct = limit > 0 ? Math.min(100, Math.round((proj / limit) * 100)) : 0;
  const over = proj > limit;
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--fs-sm)' }}>
        <span>{label}</span>
        <strong className="num" style={{ color: over ? 'var(--sgr-danger)' : undefined }}>{formatClp(proj)} / {formatClp(limit)}</strong>
      </div>
      <div role="progressbar" aria-valuenow={proj} aria-valuemin={0} aria-valuemax={limit} aria-label={label} style={{ height: 8, borderRadius: 999, background: 'var(--sgr-bg-muted)', marginTop: 6, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: over ? 'var(--sgr-danger)' : pct > 85 ? 'var(--sgr-warning)' : 'var(--sgr-success)' }} />
      </div>
      <div style={{ display: 'flex', gap: 'var(--sp-3)', fontSize: 'var(--fs-xs)', color: 'var(--sgr-text-muted)', marginTop: 4, flexWrap: 'wrap' }}>
        <span>Actual <b className="num">{formatClp(current)}</b></span>
        <span>Comprometida <b className="num">{formatClp(committed)}</b></span>
        {over && <Badge tone="rejected">Supera el límite → requiere Gerencia</Badge>}
      </div>
    </div>
  );
}

export function FundBalanceCard({ title, amount, hint, tone }: { title: string; amount: number; hint?: string; tone?: 'ok' | 'pending' | 'rejected' | 'neutral' }) {
  return (
    <Card>
      <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--sgr-text-muted)' }}>{title}</div>
      <div className="num" style={{ fontSize: 'var(--fs-2xl)', fontWeight: 600, color: tone === 'rejected' ? 'var(--sgr-danger)' : tone === 'ok' ? 'var(--sgr-success)' : undefined }}>{formatClp(amount)}</div>
      {hint && <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--sgr-text-muted)' }}>{hint}</div>}
    </Card>
  );
}

export function DeadlineChip({ deadline, clockState, remainingDays }: { deadline: string; clockState: string; remainingDays?: number | null }) {
  if (clockState === 'VENCIDO') return <Badge tone="overdue">Vencido el {formatDate(deadline)}</Badge>;
  if (clockState === 'DETENIDO') return <Badge tone="review">Reloj detenido (en revisión)</Badge>;
  if (clockState === 'CERRADO') return <Badge tone="neutral">Cerrado</Badge>;
  const d = remainingDays ?? 0;
  return <Badge tone={d <= 1 ? 'observed' : 'pending'}>{d <= 0 ? 'Vence hoy' : d === 1 ? 'Vence mañana' : `Vence en ${d} días`} · {formatDate(deadline)}</Badge>;
}

export type PerDiemLine = { zone: string; concept: string; quantity: number };
export type PerDiemMatrix = { concepts: { code: string; name: string }[]; zones: { code: string; name: string; is_active: boolean; regions: string[]; rates: Record<string, { amount_clp: number } | null> }[] };

/** Selección explícita de cantidades por concepto y zona (§10.3). Nunca infiere comidas por horarios. */
export function PerDiemQuantitySelector({ matrix, zone, lines, onZone, onChange }: { matrix: PerDiemMatrix; zone: string; lines: PerDiemLine[]; onZone: (z: string) => void; onChange: (lines: PerDiemLine[]) => void }) {
  const z = matrix.zones.find((x) => x.code === zone);
  const qty = (concept: string) => lines.find((l) => l.zone === zone && l.concept === concept)?.quantity ?? 0;
  const set = (concept: string, q: number) => {
    const rest = lines.filter((l) => !(l.zone === zone && l.concept === concept));
    onChange(q > 0 ? [...rest, { zone, concept, quantity: q }] : rest);
  };
  const total = lines.reduce((acc, l) => acc + l.quantity * (matrix.zones.find((x) => x.code === l.zone)?.rates[l.concept]?.amount_clp ?? 0), 0);
  return (
    <div>
      <label style={{ fontSize: 'var(--fs-sm)', fontWeight: 600 }}>Zona
        <select value={zone} onChange={(e) => onZone(e.target.value)} style={{ height: 36, marginLeft: 8 }}>{matrix.zones.filter((x) => x.is_active).map((x) => <option key={x.code} value={x.code}>{x.name} ({x.regions.join(', ')})</option>)}</select>
      </label>
      <table style={{ width: '100%', marginTop: 'var(--sp-3)', fontSize: 'var(--fs-sm)' }}>
        <tbody>
          {matrix.concepts.map((c) => {
            const rate = z?.rates[c.code]?.amount_clp ?? 0;
            const q = qty(c.code);
            return (
              <tr key={c.code}>
                <td style={{ padding: '6px 0' }}>{c.name}</td>
                <td className="num" style={{ color: 'var(--sgr-text-muted)' }}>{formatClp(rate)}</td>
                <td><input type="number" min={0} max={60} value={q} onChange={(e) => set(c.code, Math.max(0, Math.min(60, Number.parseInt(e.target.value || '0', 10))))} aria-label={`Cantidad ${c.name}`} style={{ width: 70, height: 34, textAlign: 'center' }} /></td>
                <td className="num" style={{ textAlign: 'right' }}><b>{formatClp(q * rate)}</b></td>
              </tr>
            );
          })}
        </tbody>
        <tfoot><tr><td colSpan={3}><b>Total viáticos</b></td><td className="num" style={{ textAlign: 'right' }}><b>{formatClp(total)}</b></td></tr></tfoot>
      </table>
      {lines.some((l) => l.zone !== zone) && <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--sgr-text-muted)' }}>Incluye líneas de otras zonas: {lines.filter((l) => l.zone !== zone).map((l) => `${l.zone} ${l.concept}×${l.quantity}`).join(', ')}</div>}
    </div>
  );
}

export function KeyValue({ rows }: { rows: Array<[string, ReactNode]> }) {
  return (
    <table style={{ fontSize: 'var(--fs-sm)', width: '100%' }}><tbody>
      {rows.map(([k, v]) => <tr key={k}><td style={{ color: 'var(--sgr-text-muted)', padding: '4px 0', width: '45%' }}>{k}</td><td className="num" style={{ textAlign: 'right' }}>{v}</td></tr>)}
    </tbody></table>
  );
}

export function RequestStatus({ status }: { status: string }) {
  return <StatusBadge status={status} />;
}

/** Subtipos de alimentación que el extractor puede sugerir (schema v1.1, QA-006); el gasto persiste el código de `rules.subtypes` de la categoría. */
export const FOOD_SUBTYPES = [{ code: 'desayuno', name: 'Desayuno' }, { code: 'almuerzo', name: 'Almuerzo' }, { code: 'cena', name: 'Cena' }, { code: 'otro', name: 'Otro' }] as const;
